import Papa from 'papaparse';

export const checkIfFileExists = (searchString, templateList) => {
    if (templateList && templateList.length) {
        for (let i = 0; i < templateList.length; i++) {
            const fileName = templateList[i].filename;

            if (fileName === searchString) {
                // there exists a file with same name as well as extension
                return {
                    isNameWithExtensionSame: true,
                    isNameWithoutExtensionSame: true,
                };
            } else {
                const fileNameParts = fileName.split('.');
                const searchStringParts = searchString.split('.');
                if (
                    fileNameParts.length >= 2 &&
                    searchStringParts.length >= 2
                ) {
                    const fileNameWithoutExtension = fileNameParts
                        .slice(0, fileNameParts.length - 1)
                        .join('.');
                    const searchStringWithOutExtension = searchStringParts
                        .slice(0, searchStringParts.length - 1)
                        .join('.');

                    if (
                        fileNameWithoutExtension ===
                        searchStringWithOutExtension
                    ) {
                        // there exists a file with same name but different extension
                        return {
                            isNameWithExtensionSame: false,
                            isNameWithoutExtensionSame: true,
                        };
                    }
                }
            }
        }
    }
    // no matching file present
    return {
        isNameWithExtensionSame: false,
        isNameWithoutExtensionSame: false,
    };
};

// Function to read file content
export const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            resolve(event.target.result);
        };

        reader.onerror = (error) => {
            reject(error);
        };

        // Read the file as text
        reader.readAsText(file);
    });
};

const containsSpecialCharacters = (variable) => {
    const regex = /[!@#$%^&*(),.?":{}|<>]/; // Matches special characters: ! @ # $ % ^ & * ( ) , . ? " : { } | < >
    return regex.test(variable);
};

const validateCSVSchemaContent = (content) => {
    // Configure PapaParse to treat the appropriate row as headers
    const parsed = Papa.parse(content, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        beforeFirstChunk: function (chunk) {
            const rows = chunk.split(/\r\n|\r|\n/);
            return rows.slice(1).join('\n');
        },
    });

    if (parsed.data.length === 0) {
        return ['No data in file.'];
    }

    const errors = [];
    const requiredFields = [
        'Field Name',
        'Question',
        'Verbosity',
        'Sub_Field Count',
        'Multiple',
    ];

    // Check headers
    const headers = parsed.meta.fields;
    requiredFields.forEach((field) => {
        if (!headers.includes(field)) {
            errors.push(`Missing \"${field}\" header.`);
        }
    });

    // Validate each data row
    parsed.data.forEach((row, index) => {
        requiredFields.forEach((field) => {
            if (row[field] == null || row[field].toString().trim() === '') {
                errors.push(`Row ${index + 1}: \"${field}\" cannot be empty.`);
            }
        });

        // Check for special characters in "Field Name" if "Multiple" is set to 'yes'
        if (row['Multiple']?.toString().trim().toLowerCase() === 'yes') {
            const fieldName = row['Field Name']?.toString().trim();
            if (containsSpecialCharacters(fieldName)) {
                errors.push(
                    `Row ${
                        index + 1
                    }: Field name '${fieldName}' with multiple set to true contains special characters.`
                );
            }
        }
    });

    return errors.length > 0 ? errors : ['Validation Successful!'];
};

const validatePythonContentFormat = (content) => {
    // Check if content contains a class definition named "Main"
    const classRegex = /class\s+Main\s*\(\s*BaseModel\s*\):/;
    let errors = [];
    if (content.length === 0) {
        return ['No data in file.'];
    }

    if (!classRegex.test(content)) {
        errors.push("Class definition 'Main(BaseModel)' not found.");
    }

    // Extract variable definitions within the class
    const variableRegex =
        /(\w+)\s*:\s*(Optional\[\s*(?:List\[\s*\w+\s*\]|str|int|float)\s*\]|List\[\s*(?:\w+\s*(?:,\s*\w+\s*)*)?\]| str|int|float)\s*=\s*Field\s*\(\s*description\s*=\s*(?:"(.*?)"|f"(.*?)"|'(.*?)'|f'(.*?)')/g;
    const variableMatches = [...content.matchAll(variableRegex)];

    // Check for list variable names with special characters and missing descriptions
    variableMatches.forEach((match) => {
        const [
            ,
            variableName,
            ,
            description1,
            description2,
            description3,
            description4,
        ] = match;
        // Check for description field
        const description =
            description1 || description2 || description3 || description4;

        // Check if the variable has special characters and is a List
        if (/List/.test(match[2]) && containsSpecialCharacters(variableName)) {
            errors.push(
                `List variable '${variableName}' has special characters.`
            );
        }

        // Check if description is missing
        if (!description) {
            errors.push(`Variable '${variableName}' is missing a description.`);
        }
    });

    return errors.length > 0 ? errors : ['Validation Successful!'];
};

//function to validate all the uploaded template
export const validateFile = async (fileName, fileContent, setError) => {
    try {
        // Determine the file extension
        const fileExtension = fileName.split('.').pop().toLowerCase();
        let validationResult = [];

        // Call the appropriate validation function based on file extension
        if (fileExtension === 'csv') {
            validationResult = validateCSVSchemaContent(fileContent); // Assume validateCSVSchemaContent returns an array
        } else if (fileExtension === 'py') {
            validationResult = validatePythonContentFormat(fileContent); // Assume validatePythonContentFormat returns an array
        } else {
            setError(
                'Unsupported file type. Only CSV and Python file are allowed.'
            );
            return { isValid: false };
        }

        // Check validation results
        if (
            validationResult.length === 1 &&
            validationResult[0] === 'Validation Successful!'
        ) {
            console.log('Validation complete');
            return { isValid: true }; // Return a positive validation result
        } else {
            // Use the validationResult array to set the error message with detailed info
            setError(
                `Uploaded file ${fileName} does not follow the required format:\n` +
                    validationResult.join(`\n`)
            );
            return { isValid: false }; // Return a negative validation result with errors
        }
    } catch (error) {
        console.error('Error reading file:', error);
        setError('Error reading file. Please try again.');
        return { isValid: false };
    }
};
